/**
 * DevTools detection and feature utilization
 * Detects when DevTools are open to enable enhanced logging features
 */

/**
 * DevTools state information
 */
export interface DevToolsState {
  isOpen: boolean;
  orientation: 'horizontal' | 'vertical' | 'undocked' | 'unknown';
}

type DevToolsListener = (state: DevToolsState) => void;

/**
 * DevTools detector using multiple detection strategies
 */
class DevToolsDetector {
  private _isOpen = false;
  private _orientation: DevToolsState['orientation'] = 'unknown';
  private _listeners: Set<DevToolsListener> = new Set();
  private _checkInterval: ReturnType<typeof setInterval> | null = null;
  private _threshold = 160;

  constructor() {
    // Auto-start detection in browser environment
    if (typeof window !== 'undefined') {
      this.startDetection();
    }
  }

  get isOpen(): boolean {
    return this._isOpen;
  }

  get state(): DevToolsState {
    return {
      isOpen: this._isOpen,
      orientation: this._orientation,
    };
  }

  /**
   * Subscribe to DevTools state changes
   */
  subscribe(listener: DevToolsListener): () => void {
    this._listeners.add(listener);
    // Immediately notify with current state
    listener(this.state);
    return () => this._listeners.delete(listener);
  }

  /**
   * Start DevTools detection
   */
  startDetection(): void {
    if (typeof window === 'undefined') return;
    if (this._checkInterval) return;

    // Initial check
    this.check();

    // Periodic check (DevTools state can change)
    this._checkInterval = setInterval(() => this.check(), 500);

    // Also check on resize
    window.addEventListener('resize', this.handleResize);
  }

  /**
   * Stop DevTools detection
   */
  stopDetection(): void {
    if (this._checkInterval) {
      clearInterval(this._checkInterval);
      this._checkInterval = null;
    }
    if (typeof window !== 'undefined') {
      window.removeEventListener('resize', this.handleResize);
    }
  }

  private handleResize = (): void => {
    this.check();
  };

  /**
   * Check if DevTools are open using multiple strategies
   */
  private check(): void {
    const wasOpen = this._isOpen;
    const wasOrientation = this._orientation;

    // Strategy 1: Window size difference (works for docked DevTools)
    const widthThreshold = window.outerWidth - window.innerWidth > this._threshold;
    const heightThreshold = window.outerHeight - window.innerHeight > this._threshold;

    if (widthThreshold) {
      this._isOpen = true;
      this._orientation = 'vertical';
    } else if (heightThreshold) {
      this._isOpen = true;
      this._orientation = 'horizontal';
    } else {
      // Strategy 2: console.log timing (slower when DevTools open)
      // This is less reliable but works for undocked DevTools
      this._isOpen = this.checkWithTiming();
      if (this._isOpen) {
        this._orientation = 'undocked';
      } else {
        this._orientation = 'unknown';
      }
    }

    // Strategy 3: Firebug detection (legacy)
    if (!this._isOpen && typeof window !== 'undefined' && (window as any).Firebug?.chrome?.isInitialized) {
      this._isOpen = true;
    }

    // Notify listeners if state changed
    if (wasOpen !== this._isOpen || wasOrientation !== this._orientation) {
      this.notifyListeners();
    }
  }

  /**
   * Check using console timing
   * When DevTools are open, toString is called on logged objects
   */
  private checkWithTiming(): boolean {
    const element = new Image();
    let devtoolsOpen = false;

    Object.defineProperty(element, 'id', {
      get: function () {
        devtoolsOpen = true;
        return '';
      },
    });

    // This will trigger the getter only if DevTools are open and inspecting
    console.debug('%c', element as any);

    return devtoolsOpen;
  }

  private notifyListeners(): void {
    const state = this.state;
    this._listeners.forEach((listener) => {
      try {
        listener(state);
      } catch (e) {
        // Ignore listener errors
      }
    });
  }

  /**
   * Manually set DevTools state (useful for testing or manual override)
   */
  setManualState(isOpen: boolean): void {
    const wasOpen = this._isOpen;
    this._isOpen = isOpen;
    if (wasOpen !== isOpen) {
      this.notifyListeners();
    }
  }
}

/**
 * Singleton instance for DevTools detection
 */
export const devToolsDetector = new DevToolsDetector();

/**
 * Check if console supports styling
 */
export function supportsConsoleStyling(): boolean {
  if (typeof window === 'undefined') return false;

  // Check for Chrome, Firefox, Safari
  const isChrome = /Chrome/.test(navigator.userAgent);
  const isFirefox = /Firefox/.test(navigator.userAgent);
  const isSafari = /Safari/.test(navigator.userAgent) && !/Chrome/.test(navigator.userAgent);

  return isChrome || isFirefox || isSafari;
}

/**
 * Check if console supports grouping
 */
export function supportsConsoleGrouping(): boolean {
  return typeof console.group === 'function' && typeof console.groupEnd === 'function';
}

/**
 * Check if console supports tables
 */
export function supportsConsoleTable(): boolean {
  return typeof console.table === 'function';
}

/**
 * Check if console supports timing
 */
export function supportsConsoleTiming(): boolean {
  return typeof console.time === 'function' && typeof console.timeEnd === 'function';
}

/**
 * Get all available DevTools features
 */
export function getDevToolsFeatures(): {
  styling: boolean;
  grouping: boolean;
  table: boolean;
  timing: boolean;
  trace: boolean;
} {
  return {
    styling: supportsConsoleStyling(),
    grouping: supportsConsoleGrouping(),
    table: supportsConsoleTable(),
    timing: supportsConsoleTiming(),
    trace: typeof console.trace === 'function',
  };
}
